import React, { useEffect, useState } from 'react'
import { translateRatingToDecimal } from '@lib/utils/translateRatingToDecimal'
import StarRatings from 'react-star-ratings'
import cn from 'classnames'

import s from './ReviewStars.module.scss'

type ReviewStarsProps = {
    rating?: number
    mobile?: boolean
    /** Defaults to 5. Use 1 for PLP compact rating next to price. */
    numberOfStars?: number
    /** When true, shows average and optional review count beside the star(s). */
    showAverage?: boolean
    reviewCount?: number
    className?: string
}

const ratingSpecs = {
    starDimension: '18px',
    starSpacing: '4px',
    starEmptyColor: 'white',
    starRatedColor: '#696158',
    svgIconViewBox: '0 0 27 25'
}

const ratingSpecsMobile = {
    starDimension: '16px',
    starSpacing: '2px',
    starEmptyColor: 'white',
    starRatedColor: '#696158',
    svgIconViewBox: '0 0 25 23'
}

const ratingSpecsCompact = {
    starDimension: '16px',
    starSpacing: '0px',
    starEmptyColor: 'white',
    starRatedColor: '#696158',
    svgIconViewBox: '0 0 27 25'
}

const svgPath =
    'M21.2036 8.78907C22.8223 8.91661 25.4585 9.21724 25.5972 9.59986C25.921 10.4926 19.0946 14.911 19.0946 14.911C19.0946 14.911 22.0638 22.5817 21.0093 23.5109C20.1398 24.2579 13.5817 19.1016 13.5817 19.1016C13.5817 19.1016 6.57038 23.7477 6.15414 23.5018C4.94241 22.773 8.06885 14.9019 8.06885 14.9019C8.06885 14.9019 -0.607465 10.2922 1.56624 9.59075C4.50767 8.6342 9.81706 8.67975 9.81706 8.67975C9.81706 8.67975 12.1018 1 13.5817 1C14.7472 1 17.3464 8.68886 17.3464 8.68886C17.3464 8.68886 19.2334 8.68886 21.2036 8.78907Z'

const ReviewStars: React.FC<ReviewStarsProps> = ({
    rating,
    mobile = false,
    numberOfStars = 5,
    showAverage = false,
    reviewCount,
    className
}) => {
    const [hasMounted, setHasMounted] = useState(false)
    const starAverage = translateRatingToDecimal(rating, true) as
        number | undefined
    const isCompact = numberOfStars === 1
    const specs = isCompact
        ? ratingSpecsCompact
        : mobile
          ? ratingSpecsMobile
          : ratingSpecs

    // react-star-ratings fills relative to numberOfStars (0..numberOfStars)
    const starFillRating =
        starAverage == null ? 0 : isCompact ? starAverage / 5 : starAverage

    useEffect(() => {
        setHasMounted(true)
    }, [])

    if (!hasMounted) {
        return (
            <div className={cn(s['rating-container'], className)} aria-hidden />
        )
    }

    const averageLabel =
        starAverage == null ? null : Number(starAverage).toFixed(1)

    return (
        <div
            className={cn(
                s['rating-container'],
                { [s['rating-container-compact']]: isCompact || showAverage },
                className
            )}
            aria-label={
                averageLabel
                    ? `Valoración ${averageLabel} de 5${
                          reviewCount != null && reviewCount > 0
                              ? `, ${reviewCount} reseñas`
                              : ''
                      }`
                    : undefined
            }
        >
            <StarRatings
                {...specs}
                numberOfStars={numberOfStars}
                rating={starFillRating}
                svgIconPath={svgPath}
            />
            {showAverage && averageLabel != null && (
                <span className={s['rating-average']}>{averageLabel}</span>
            )}
        </div>
    )
}

export default ReviewStars
